import { IRepository } from '../../infrastructure/repository.interface';
import { Injectable } from '@angular/core';
import { ErrorCodes, fileBooleanToString, TableModel, ToastDuration, ToastService } from '@shared';
import { WorkspaceDatabaseProvider } from '../workspace-database.provider';
import { RxDocument } from 'rxdb';
import { Observable, throwError, catchError, map } from 'rxjs';
import { WorkspaceDatabaseKeys } from '../schemas';
import { LoggingService } from '@shared/logging';
import { addRxPlugin } from 'rxdb';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { QueryOperatorsEnum } from '../models';
addRxPlugin(RxDBQueryBuilderPlugin);

@Injectable()
export class TableRepository implements IRepository<TableModel> {
	public constructor(
		private dbProvider: WorkspaceDatabaseProvider,
		private loggerService: LoggingService,
		private toastService: ToastService
	) {}

	public async insert(entity: TableModel): Promise<void> {
		const database = this.dbProvider.getCurrentDatabaseInstance();

		await database.tables.insert(entity);
	}

	public async bulkInsert(entites: TableModel[]): Promise<void> {
		const database = this.dbProvider.getCurrentDatabaseInstance();

		this.loggerService.info('WorkfileNumberOfImportedTables', entites.length);

		await database.tables.bulkInsert(entites);
	}

	public getTableChildIdsByName$(tableName: string): Observable<number[] | null> {
		const database = this.dbProvider.getDatabaseCollection(WorkspaceDatabaseKeys.tablesKey);
		return database
			.findOne()
			.where(WorkspaceDatabaseKeys.tables.tableName)
			.eq(tableName)
			.$.pipe(
				map((table: RxDocument<TableModel>) =>
					table && table.toJSON().child_table_ids !== ''
						? table.toJSON().child_table_ids.split(',').map(Number)
						: null
				)
			);
	}

	public tableHasChildren$(tableName: string): Observable<boolean> {
		return this.getTableChildIdsByName$(tableName).pipe(
			map((tableChildrendIds: number[] | null) => tableChildrendIds !== null)
		);
	}

	public getChildTablesMetadataByIds$(tableIds: number[]): Observable<TableModel[]> {
		if (!tableIds) {
			this.loggerService.error(ErrorCodes.NullInputParam, 'No child table ids provided for retrieving tables');
			throw new Error('No child table ids provided for retrieving tables');
		}

		const database = this.dbProvider.getDatabaseCollection(WorkspaceDatabaseKeys.tablesKey);
		return (
			database
				.find()
				.where(WorkspaceDatabaseKeys.tables.tableId)
				// the order of the arguments in the array of an $in clause does not reflect the order of how the documents are retrieved
				.in(tableIds)
				.$.pipe(
					map((childTables: RxDocument<any>[]) =>
						childTables
							.map((childTable: RxDocument<any>) => childTable.toJSON())
							.sort((a, b) => tableIds.indexOf(a.table_id) - tableIds.indexOf(b.table_id))
					)
				)
		);
	}

	public getReferenceData$(tableName: string): Observable<any[]> {
		const database = this.dbProvider.getDatabaseCollection(WorkspaceDatabaseKeys.referenceTableKey); // Adjust the collection if needed
		return database
			.find()
			.where(WorkspaceDatabaseKeys.tables.tableName.toLowerCase())
			.eq(tableName)
			.$ 
			.pipe(
				map((referenceData: RxDocument<any>[]) => {
					return referenceData.map((data: RxDocument<any>) => data.toJSON());
			}),
			catchError((error) => {
				return throwError(() => new Error(`Error retrieving reference data: ${error}`));
			})
		);
	}

	public getTables$(): Observable<TableModel[]> {
		const database = this.dbProvider.getDatabaseCollection(WorkspaceDatabaseKeys.tablesKey);

		return database.find().$.pipe(
			map((tables: RxDocument<any>[]) =>
				tables.map((table: RxDocument<any>) => {
					const t = table.toJSON();
					delete t['_id']; // eslint-disable-line
					return t;
				})
			)
		);
	}

	public getIntervalTables$(skippedTableNames?: string[]): Observable<TableModel[]> {
		const collection = this.dbProvider.getDatabaseCollection(WorkspaceDatabaseKeys.tablesKey);

		if (!skippedTableNames) {
			skippedTableNames = [];
		}
		const conditions = skippedTableNames.map((skippedTableName) => ({
			[WorkspaceDatabaseKeys.columns.tableName]: { [QueryOperatorsEnum.notEqual]: skippedTableName }
		}));

		return collection.find({
			selector: {
				$and: [
					...conditions,
					{ [WorkspaceDatabaseKeys.tables.isIntervalRelated]: { $eq: fileBooleanToString(true) } }
				]
			}
		}).$;
	}

	public getTableTitle$(tableName: string): Observable<string> {
		const database = this.dbProvider.getDatabaseCollection(WorkspaceDatabaseKeys.tablesKey);
		return database
			.findOne()
			.where(WorkspaceDatabaseKeys.tables.tableName.toLowerCase())
			.eq(tableName)
			.$.pipe(map((table: RxDocument<TableModel>) => table.toJSON().tab_title));
	}

	public getTableMetadata$(tableName: string): Observable<TableModel> {
		if (tableName === null || tableName === undefined) {
			this.loggerService.error(ErrorCodes.MissingTableName, `Table name is required. Now is ${tableName}`);
			return throwError(() => ErrorCodes.MissingTableName);
		}

		return this.dbProvider
			.getDatabaseCollection(WorkspaceDatabaseKeys.tablesKey)
			.findOne()
			.where(WorkspaceDatabaseKeys.tables.tableName.toLowerCase())
			.eq(tableName)
			.$.pipe(map((table: RxDocument<TableModel>) => (table ? table.toJSON() : null)));
	}

	public getStandardTable$(tableName: string): Observable<string | null> {
		return this.getTableMetadata$(tableName).pipe(
			map((tableMetadata: TableModel) => (tableMetadata.standard_table ? tableMetadata.standard_table : null)),
			catchError((error) =>
				this.toastService.presentError(
					`${error}: Table name is required. Now is ${tableName}`,
					ToastDuration.Slow
				)
			)
		);
	}

	public isChildTableOfDrillHole$(childTableName: string): Observable<boolean> {
		return this.getTables$().pipe(
			map((tables: TableModel[]) => {
				const drillHoleTable = tables.find(
					(item) => item.table_name.toLowerCase() === WorkspaceDatabaseKeys.tables.drillHole.toLowerCase()
				);
				if (!drillHoleTable) {
					return false;
				}

				const currentTable = tables.find(
					(item) => item.table_name.toLowerCase() === childTableName.toLowerCase()
				);

				if (
					drillHoleTable.standard_table &&
					drillHoleTable.standard_table.toLowerCase() === currentTable.table_name.toLowerCase()
				) {
					return true;
				}

				if (!drillHoleTable.child_table_ids || drillHoleTable.child_table_ids === '') {
					return false;
				}

				const drillHoleChildTablesIds = drillHoleTable.child_table_ids.split(',').map(Number);
				return drillHoleChildTablesIds.includes(currentTable.table_id);
			})
		);
	}
}
